import { Property, PropertySearchParams, PropertySearchResponse } from '../types/property';
import { TeamMember } from '../data/mockTeam';

const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:3001/api');
const PROPERTY_SEARCH_CACHE_TTL = 60 * 1000;
const PROPERTY_FILTER_OPTIONS_CACHE_TTL = 5 * 60 * 1000;
const propertySearchCache = new Map<string, { data: PropertySearchResponse; timestamp: number }>();
const inFlightPropertySearches = new Map<string, Promise<PropertySearchResponse>>();
let propertyFilterOptionsCache: { data: { cities: string[]; neighborhoods: string[] }; timestamp: number } | null = null;
let inFlightPropertyFilterOptionsRequest: Promise<{ cities: string[]; neighborhoods: string[] }> | null = null;

function buildQueryString(params: PropertySearchParams = {}): string {
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue;
        query.append(key, String(item));
      }
      continue;
    }

    query.set(key, String(value));
  }

  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

function getPropertySearchCacheKey(params: PropertySearchParams = {}): string {
  return buildQueryString(params) || '__default__';
}

function getCachedPropertySearch(key: string): PropertySearchResponse | null {
  const cached = propertySearchCache.get(key);

  if (!cached) {
    return null;
  }

  if (Date.now() - cached.timestamp > PROPERTY_SEARCH_CACHE_TTL) {
    propertySearchCache.delete(key);
    return null;
  }

  return cached.data;
}

function setCachedPropertySearch(key: string, data: PropertySearchResponse) {
  propertySearchCache.set(key, {
    data,
    timestamp: Date.now(),
  });
}

function normalizePropertySearchResponse(
  payload: unknown,
  params: PropertySearchParams = {}
): PropertySearchResponse {
  if (Array.isArray(payload)) {
    const page = params.page || 1;
    const pageSize = params.pageSize || payload.length || 12;
    const total = payload.length;

    return {
      items: payload as Property[],
      total,
      page,
      pageSize,
      totalPages: Math.max(1, Math.ceil(total / Math.max(pageSize, 1))),
    };
  }

  const data = payload as Partial<PropertySearchResponse> | null;

  if (data && Array.isArray(data.items)) {
    return {
      items: data.items,
      total: typeof data.total === 'number' ? data.total : data.items.length,
      page: typeof data.page === 'number' ? data.page : params.page || 1,
      pageSize: typeof data.pageSize === 'number' ? data.pageSize : params.pageSize || data.items.length || 12,
      totalPages: typeof data.totalPages === 'number' ? data.totalPages : 1,
    };
  }

  return {
    items: [],
    total: 0,
    page: params.page || 1,
    pageSize: params.pageSize || 12,
    totalPages: 1,
  };
}

export async function fetchProperties(params: PropertySearchParams = {}): Promise<PropertySearchResponse> {
  const cacheKey = getPropertySearchCacheKey(params);
  const cached = getCachedPropertySearch(cacheKey);

  if (cached) {
    return cached;
  }

  const inFlight = inFlightPropertySearches.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
    try {
      const response = await fetch(`${API_URL}/properties${buildQueryString(params)}`);
      if (!response.ok) {
        throw new Error('Failed to fetch properties');
      }

      const payload = await response.json();
      const data = normalizePropertySearchResponse(payload, params);
      setCachedPropertySearch(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching properties:', error);
      return {
        items: [],
        total: 0,
        page: params.page || 1,
        pageSize: params.pageSize || 12,
        totalPages: 1,
      };
    } finally {
      inFlightPropertySearches.delete(cacheKey);
    }
  })();

  inFlightPropertySearches.set(cacheKey, request);

  try {
    return await request;
  } catch {
    return {
      items: [],
      total: 0,
      page: params.page || 1,
      pageSize: params.pageSize || 12,
      totalPages: 1,
    };
  }
}

export function prefetchProperties(params: PropertySearchParams = {}) {
  void fetchProperties(params);
}

export function peekCachedProperties(params: PropertySearchParams = {}): PropertySearchResponse | null {
  return getCachedPropertySearch(getPropertySearchCacheKey(params));
}

export async function fetchPropertyById(id: string): Promise<Property | null> {
  try {
    const response = await fetch(`${API_URL}/properties/${id}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch property');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching property:', error);
    return null;
  }
}

export async function fetchTeamMembers(): Promise<TeamMember[]> {
  try {
    const response = await fetch(`${API_URL}/team`);
    if (!response.ok) {
      throw new Error('Failed to fetch team members');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching team members:', error);
    return [];
  }
}

export async function fetchPropertyFilterOptions(): Promise<{ cities: string[]; neighborhoods: string[] }> {
  if (
    propertyFilterOptionsCache &&
    Date.now() - propertyFilterOptionsCache.timestamp < PROPERTY_FILTER_OPTIONS_CACHE_TTL
  ) {
    return propertyFilterOptionsCache.data;
  }

  if (inFlightPropertyFilterOptionsRequest) {
    return inFlightPropertyFilterOptionsRequest;
  }

  const request = (async () => {
    try {
      const response = await fetch(`${API_URL}/property-filters`);
      if (!response.ok) {
        throw new Error('Failed to fetch property filter options');
      }

      const data = await response.json();
      const normalizedData = {
        cities: Array.isArray(data?.cities) ? data.cities : [],
        neighborhoods: Array.isArray(data?.neighborhoods) ? data.neighborhoods : [],
      };

      propertyFilterOptionsCache = {
        data: normalizedData,
        timestamp: Date.now(),
      };

      return normalizedData;
    } catch (error) {
      console.error('Error fetching property filter options:', error);
      return {
        cities: [],
        neighborhoods: [],
      };
    } finally {
      inFlightPropertyFilterOptionsRequest = null;
    }
  })();

  inFlightPropertyFilterOptionsRequest = request;
  return request;
}
